import { readEventId } from "../store/eventIdentity";
import { removeReservationLocal } from "../../../store/reservationsSlice";
import { ultraSessionLog } from "../../debug/ultraSessionDebug";

function resolvePatchBus(store, patchBus) {
   return patchBus || store?.patchBus || null;
}

/**
 * Socket upserts must prefer the slot derived from the incoming row
 * (new startTime) over the store's previous slot — otherwise a remote
 * move paints into the old cell and leaves a lasting ghost patch.
 */
export function resolveSocketUpsertSlotKey({
   row,
   eventId = "",
   getStoredSlotKey = null,
   resolveSlotKeyFromRow = null,
} = {}) {
   const fromRow =
      typeof resolveSlotKeyFromRow === "function"
         ? String(resolveSlotKeyFromRow(row) || "").trim()
         : "";
   if (fromRow) return fromRow;

   const id = String(eventId || readEventId(row) || "").trim();
   if (!id || typeof getStoredSlotKey !== "function") return "";
   return String(getStoredSlotKey(id) || "").trim();
}

/** Drop this id's prior upserts/removes, then enforce a remove before a move upsert. */
export function compactPatchBusBeforeSlotMove(patchBus, { reservationId } = {}) {
   if (!patchBus || typeof patchBus.replacePatches !== "function") return;

   const id = String(reservationId || "").trim();
   if (!id) return;

   const kept = [];
   for (const patch of patchBus.getPatches?.() ?? []) {
      if (!patch || typeof patch !== "object") continue;
      if (patch.op === "upsert") {
         const pid = String(patch.item?.id || "").trim();
         if (pid === id) continue;
         kept.push(patch);
         continue;
      }
      if (patch.op === "remove" && String(patch.reservationId || "").trim() === id) {
         continue;
      }
      kept.push(patch);
   }
   kept.push({ op: "remove", reservationId: id });
   patchBus.replacePatches(kept);
   logMutation("compactPatchBusBeforeSlotMove", { reservationId: id });
}

/** Collect real + tmp ids and slot key for session-created reservations. */
export function collectSessionRemovalScope(store, patchBus, reservationId) {
   const id = String(reservationId || "").trim();
   const ids = new Set(id ? [id] : []);
   let slotKey = String(store?.getEventSlotKey?.(id) || "").trim();
   const patches = patchBus?.getPatches?.() ?? [];

   for (const patch of patches) {
      if (!patch || typeof patch !== "object") continue;
      if (patch.op === "upsert") {
         const pid = String(patch.item?.id || "").trim();
         const pslot = String(patch.slotKey || "").trim();
         if (pid === id) slotKey = slotKey || pslot;
      }
      if (patch.op === "replaceId") {
         const from = String(patch.fromId || "").trim();
         const to = String(patch.toId || "").trim();
         if (to === id && from) ids.add(from);
         if (from === id && to) ids.add(to);
      }
   }

   if (!slotKey) {
      for (const patch of patches) {
         if (patch?.op !== "upsert") continue;
         const pid = String(patch.item?.id || "").trim();
         const pslot = String(patch.slotKey || "").trim();
         if (pid && ids.has(pid) && pslot) {
            slotKey = pslot;
            break;
         }
      }
   }

   if (slotKey) {
      for (const patch of patches) {
         if (patch?.op !== "upsert") continue;
         if (String(patch.slotKey || "").trim() !== slotKey) continue;
         const pid = String(patch.item?.id || "").trim();
         if (pid) ids.add(pid);
      }
      for (const aliasId of store?.getSlotEventIds?.(slotKey) || []) {
         const normalized = String(aliasId || "").trim();
         if (normalized) ids.add(normalized);
      }
   }

   return {
      slotKey,
      ids: [...ids].filter(Boolean),
   };
}

/** Map tmp / stale ids to the server id (replaceId chain + store slot). */
export function resolveApiReservationId(store, patchBus, reservationId) {
   let id = String(reservationId || "").trim();
   if (!id) return "";

   const patches = patchBus?.getPatches?.() ?? [];

   for (let hop = 0; hop < 8; hop += 1) {
      let promoted = "";
      for (const patch of patches) {
         if (patch?.op !== "replaceId") continue;
         if (String(patch.fromId || "").trim() !== id) continue;
         promoted = String(patch.toId || "").trim();
         break;
      }
      if (!promoted || promoted === id) break;
      id = promoted;
   }

   if (!id.startsWith("tmp-")) return id;

   const slotKey = String(store?.getEventSlotKey?.(id) || "").trim();
   if (slotKey) {
      for (const slotId of store?.getSlotEventIds?.(slotKey) || []) {
         const normalized = String(slotId || "").trim();
         if (normalized && !normalized.startsWith("tmp-")) return normalized;
      }
      for (const patch of patches) {
         if (patch?.op !== "upsert") continue;
         if (String(patch.slotKey || "").trim() !== slotKey) continue;
         const pid = String(patch.item?.id || "").trim();
         if (pid && !pid.startsWith("tmp-")) return pid;
      }
   }

   return id;
}

/** True when a slot still has an in-flight tmp create/paste patch. */
export function slotHasPendingOptimisticPatch(patchBus, slotKey) {
   const key = String(slotKey || "").trim();
   if (!key || !patchBus) return false;
   for (const patch of patchBus.getPatches?.() ?? []) {
      if (patch?.op !== "upsert") continue;
      if (String(patch.slotKey || "").trim() !== key) continue;
      const pid = String(patch.item?.id || "").trim();
      if (pid.startsWith("tmp-")) return true;
   }
   return false;
}

/** Drop stale optimistic patches after tmp → real id promotion. */
export function compactPatchBusAfterIdPromotion(
   patchBus,
   { fromId, toId, slotKey = "" } = {},
) {
   if (!patchBus || typeof patchBus.replacePatches !== "function") return;

   const from = String(fromId || "").trim();
   const to = String(toId || "").trim();
   const key = String(slotKey || "").trim();
   if (!from || !to) return;

   const kept = [];
   for (const patch of patchBus.getPatches?.() ?? []) {
      if (!patch || typeof patch !== "object") continue;
      if (patch.op === "upsert") {
         const pid = String(patch.item?.id || "").trim();
         const pslot = String(patch.slotKey || "").trim();
         if (pid === from && (!key || pslot === key)) continue;
      }
      if (patch.op === "remove" && String(patch.reservationId || "").trim() === to) {
         continue;
      }
      if (
         patch.op === "replaceId" &&
         String(patch.fromId || "").trim() === from &&
         String(patch.toId || "").trim() === to
      ) {
         continue;
      }
      kept.push(patch);
   }
   patchBus.replacePatches(kept);
}

function logMutation(event, data) {
   ultraSessionLog("MUTATION", event, data);
}

/** Drop stale optimistic upserts and enforce remove patches for deleted ids. */
export function compactPatchBusForRemoval(
   patchBus,
   { reservationIds, slotKey = "" } = {},
) {
   if (!patchBus || typeof patchBus.replacePatches !== "function") return;

   const idSet = new Set(
      (Array.isArray(reservationIds) ? reservationIds : [reservationIds])
         .map((value) => String(value || "").trim())
         .filter(Boolean),
   );
   const key = String(slotKey || "").trim();
   const existing = patchBus.getPatches?.() ?? [];
   const kept = [];

   for (const patch of existing) {
      if (!patch || typeof patch !== "object") continue;
      if (patch.op === "remove") {
         kept.push(patch);
         continue;
      }
      if (patch.op === "upsert") {
         const pid = String(patch.item?.id || "").trim();
         const pslot = String(patch.slotKey || "").trim();
         if (idSet.has(pid)) continue;
         if (key && pslot === key) continue;
         kept.push(patch);
         continue;
      }
      if (patch.op === "replaceId") {
         const from = String(patch.fromId || "").trim();
         const to = String(patch.toId || "").trim();
         if (idSet.has(from) || idSet.has(to)) continue;
         kept.push(patch);
         continue;
      }
      kept.push(patch);
   }

   for (const rid of idSet) {
      kept.push({ op: "remove", reservationId: rid });
   }

   patchBus.replacePatches(kept);
   logMutation("compactPatchBusForRemoval", {
      reservationIds: [...idSet],
      slotKey: key,
      before: existing.length,
      after: kept.length,
   });
}

/**
 * Optimistic upsert: CalendarStore + optional visual patch for the grid.
 * On slot move, clears this id from the old cell (remove) before painting the new one.
 */
export function applyOptimisticUpsert(
   store,
   { event, slotKey, commandId = "", patchBus = null, visualItem = null } = {},
) {
   const id = readEventId(event);
   const key = String(slotKey || "").trim();
   if (!store || !id || !key) return false;

   const prevSlot = String(store.getEventSlotKey?.(id) || "").trim();
   const moved = !!prevSlot && prevSlot !== key;

   if (commandId) store.markPendingCommand(id, commandId);
   store.upsertEvent({ event, slotKey: key, pendingCommandId: commandId });

   const bus = resolvePatchBus(store, patchBus);
   if (bus && visualItem) {
      if (moved) {
         compactPatchBusBeforeSlotMove(bus, { reservationId: id });
      }
      bus.push({ op: "upsert", slotKey: key, item: visualItem });
   }
   logMutation("applyOptimisticUpsert", {
      id,
      slotKey: key,
      prevSlot: moved ? prevSlot : null,
      moved,
      commandId: commandId || null,
      hasVisual: !!visualItem,
   });
   return true;
}

export function applyOptimisticRemove(
   store,
   {
      eventId,
      commandId = "",
      patchBus = null,
      tombstone = true,
      clearPending = true,
   } = {},
) {
   const id = String(eventId || "").trim();
   if (!store || !id) return false;

   if (commandId) store.markPendingCommand(id, commandId);
   store.removeEvent(id, { tombstone, clearPending });
   resolvePatchBus(store, patchBus)?.push?.({ op: "remove", reservationId: id });
   logMutation("applyOptimisticRemove", { id, commandId: commandId || null, tombstone });
   return true;
}

/**
 * MAX-aligned delete: socket tombstone + store remove + slot patch + Redux local.
 * Idempotent — safe if cut/delete paths call it more than once.
 */
export function applyCalendarReservationRemoval(
   {
      store,
      reservationId,
      commandId = "",
      patchBus = null,
      markSocketDeleted,
      dispatch,
      dispatchLocalRemove = true,
   } = {},
) {
   const id = String(reservationId || "").trim();
   if (!store || !id) return false;

   const bus = resolvePatchBus(store, patchBus);
   const { slotKey, ids: removalIds } = collectSessionRemovalScope(store, bus, id);
   const targets = removalIds.length ? removalIds : [id];

   if (typeof markSocketDeleted === "function") {
      markSocketDeleted(targets);
   }

   compactPatchBusForRemoval(bus, {
      reservationIds: targets,
      slotKey,
   });

   for (const targetId of targets) {
      const normalized = String(targetId || "").trim();
      if (!normalized) continue;

      const alreadyTombstoned = store.isTombstoned?.(normalized) === true;
      const hadEvent = !!store.getEventById?.(normalized);

      if (!alreadyTombstoned || hadEvent) {
         if (normalized === id && commandId) {
            store.markPendingCommand(normalized, commandId);
         }
         store.removeEvent(normalized, { tombstone: true, clearPending: true });
      }

      if (dispatchLocalRemove && typeof dispatch === "function") {
         dispatch(removeReservationLocal(normalized));
      }
   }

   logMutation("applyCalendarReservationRemoval", {
      id,
      slotKey,
      targets,
      commandId: commandId || null,
   });
   return true;
}

export function applyOptimisticReplaceId(
   store,
   {
      fromId,
      toId,
      nextEvent = null,
      slotKey = "",
      patchBus = null,
   } = {},
) {
   if (!store) return false;
   const from = String(fromId || "").trim();
   const to = String(toId || "").trim();
   if (!from || !to) return false;

   const bus = resolvePatchBus(store, patchBus);
   const toTombstoned = store.isTombstoned?.(to) === true;
   const fromGone =
      store.isTombstoned?.(from) === true && !store.getEventById?.(from);

   if (toTombstoned) {
      store.removeEvent(from, { tombstone: true, clearPending: true });
      compactPatchBusForRemoval(bus, {
         reservationIds: [from, to],
         slotKey: String(slotKey || store.getEventSlotKey?.(from) || "").trim(),
      });
      logMutation("applyOptimisticReplaceId:blocked-target-tombstone", {
         from,
         to,
      });
      return false;
   }

   if (fromGone && !store.getEventById?.(to)) {
      // User deleted optimistic create before server confirm.
      store.removeEvent(to, { tombstone: true, clearPending: true });
      compactPatchBusForRemoval(bus, {
         reservationIds: [from, to],
         slotKey: String(slotKey || "").trim(),
      });
      logMutation("applyOptimisticReplaceId:blocked-source-deleted", {
         from,
         to,
      });
      return false;
   }

   const replaced = store.replaceEventId(from, to, nextEvent, slotKey);
   if (!replaced) {
      logMutation("applyOptimisticReplaceId:rejected", { from, to });
      return false;
   }
   store.clearPendingCommand(from);
   store.clearPendingCommand(to);
   store.markLocalEcho?.(to);
   bus?.push?.({
      op: "replaceId",
      fromId: from,
      toId: to,
   });
   logMutation("applyOptimisticReplaceId", {
      from,
      to,
      slotKey: String(slotKey || "").trim(),
   });
   return true;
}

export function rollbackOptimisticRemove(
   store,
   { eventId, event, slotKey, patchBus = null, visualItem = null } = {},
) {
   const id = String(eventId || "").trim();
   const key = String(slotKey || "").trim();
   if (!store || !id || !event || !key) return false;

   store.clearTombstone(id);
   store.clearPendingCommand(id);
   store.upsertEvent({ event, slotKey: key });

   const bus = resolvePatchBus(store, patchBus);
   if (bus && visualItem) {
      bus.push({ op: "upsert", slotKey: key, item: visualItem });
   }
   logMutation("rollbackOptimisticRemove", { id, slotKey: key });
   return true;
}

export function completePendingCommand(store, eventId, echoMs = 60_000) {
   const id = String(eventId || "").trim();
   if (!store || !id) return;
   store.clearPendingCommand(id);
   store.markLocalEcho?.(id, echoMs);
}

/**
 * Optimistic create: upsert when slotKey is known, otherwise mark pending until hydrate.
 */
export function applyOptimisticCreate(
   store,
   { event, slotKey = "", commandId = "" } = {},
) {
   const id = readEventId(event);
   if (!store || !id) return false;
   const cmd = String(commandId || `create-${id}`).trim();
   const key = String(slotKey || "").trim();
   if (key) {
      return applyOptimisticUpsert(store, {
         event,
         slotKey: key,
         commandId: cmd,
      });
   }
   store.markPendingCommand(id, cmd);
   return true;
}

/** Socket / delta delete path — keep store, patch bus, Redux, and tombstones aligned. */
export function syncSocketReservationDeletes({
   store,
   reservationIds,
   markSocketDeleted,
   dispatch,
} = {}) {
   const list = Array.isArray(reservationIds)
      ? reservationIds
      : reservationIds != null
        ? [reservationIds]
        : [];
   for (const rawId of list) {
      applyCalendarReservationRemoval({
         store,
         reservationId: rawId,
         patchBus: store?.patchBus,
         markSocketDeleted,
         dispatch,
      });
   }
   logMutation("syncSocketReservationDeletes", { count: list.length, ids: list });
   return list.length;
}
